import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Sparkles, Crown, Loader2 } from "lucide-react";

const fmtDate = (ts) =>
  new Date(ts * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function Rung({ testid, icon: Icon, iconColor, title, price, duration, desc, children }) {
  return (
    <div className="border border-[#3a3a3a] bg-[#050505] p-5 flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color: iconColor }} />
        <span className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">{title}</span>
      </div>
      <div className="font-display text-3xl font-black mb-0.5">{price}</div>
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-3">{duration}</div>
      <p className="text-xs text-[#e2e2e2] mb-4 flex-1">{desc}</p>
      {children}
    </div>
  );
}

export default function PromoLadder({ coin }) {
  const [heroInfo, setHeroInfo] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/hero-coin").then((r) => { if (!cancelled) setHeroInfo(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const buy = async (sku) => {
    setBusy(sku);
    try {
      const { data } = await api.post("/payments/checkout", {
        sku,
        coin_slug: coin.slug,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setBusy(null);
      toast.error(e.response?.data?.detail || "Payment failed — try again");
    }
  };

  const isFeatured = coin.status === "featured";
  const featuredUntil = coin.featured_until ? fmtDate(coin.featured_until) : null;
  const hero = heroInfo?.hero;
  const isHero = hero?.slug === coin.slug;
  const heroTaken = !!hero && !isHero;

  const activeChip = (label) => (
    <div className="border border-[#00ff41]/50 text-[#00ff41] font-mono text-[11px] uppercase tracking-widest px-3 py-2.5 text-center">
      {label}
    </div>
  );

  const payBtn = (sku, testid, label, cls) => (
    <button
      onClick={() => buy(sku)}
      disabled={busy === sku}
      data-testid={testid}
      className={`btn-brutal font-bold uppercase tracking-wider text-xs px-4 py-2.5 inline-flex items-center justify-center gap-1.5 disabled:opacity-50 ${cls}`}
    >
      {busy === sku && <Loader2 size={12} className="animate-spin" />}
      {label}
    </button>
  );

  return (
    <section className="border border-[#cfff04] bg-[#121212] p-6 mb-6" data-testid="promo-ladder">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04] mb-1">
        // Boost visibility
      </div>
      <div className="font-display text-2xl font-bold uppercase mb-4">Pick your ladder rung.</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Rung
          testid="ladder-rung-boost"
          icon={Zap}
          iconColor="#00ff41"
          title="Fresh Boost"
          price="$9"
          duration="24 hours"
          desc="Jump straight back to the top of /explore trending. Perfect right after launch or a big update."
        >
          {isFeatured
            ? activeChip(`Active${featuredUntil ? ` — until ${featuredUntil}` : ""}`)
            : payBtn("BOOST_24H", "boost-pay", "Boost · $9", "bg-[#00ff41] text-[#050505]")}
        </Rung>
        <Rung
          testid="ladder-rung-feature"
          icon={Sparkles}
          iconColor="#cfff04"
          title="Featured"
          price="$49"
          duration="7 days"
          desc="Gold badge + top of /explore all week. ~3× more views on average."
        >
          {isFeatured
            ? activeChip(`Active${featuredUntil ? ` — until ${featuredUntil}` : ""}`)
            : payBtn("FEATURE_7D", "feature-pay", "Feature · $49", "bg-[#cfff04] text-[#050505]")}
        </Rung>
        <Rung
          testid="ladder-rung-hero"
          icon={Crown}
          iconColor="#ffd700"
          title="Homepage Hero"
          price="$149"
          duration="30 days"
          desc="Your coin showcased on the SolSpur homepage. One coin at a time — first come, first served."
        >
          {isHero ? (
            activeChip(`Yours — until ${fmtDate(hero.hero_until)}`)
          ) : heroTaken ? (
            <div
              className="border border-[#333] text-[#d0d0d0] font-mono text-[11px] uppercase tracking-widest px-3 py-2.5 text-center"
              data-testid="hero-slot-taken"
            >
              Slot taken — frees {fmtDate(hero.hero_until)}
            </div>
          ) : (
            payBtn("HERO_30D", "hero-pay", "Go Hero · $149", "bg-[#ffd700] text-[#050505]")
          )}
        </Rung>
      </div>
    </section>
  );
}
